// ─── Matrix Utilities ─────────────────────────────────────────────────────────
// All operations needed for OLS, VIF, factor analysis, correlation matrices.
// Pure JS — no dependencies.

export function matTranspose(A) {
  const rows = A.length, cols = A[0].length
  return Array.from({ length: cols }, (_, j) =>
    Array.from({ length: rows }, (_, i) => A[i][j])
  )
}

export function matMul(A, B) {
  const ra = A.length, ca = A[0].length, cb = B[0].length
  return Array.from({ length: ra }, (_, i) =>
    Array.from({ length: cb }, (_, j) =>
      Array.from({ length: ca }, (_, k) => A[i][k] * B[k][j]).reduce((s, v) => s + v, 0)
    )
  )
}

export function matVecMul(A, v) {
  return A.map(row => row.reduce((s, a, j) => s + a * v[j], 0))
}

// Gaussian elimination with partial pivoting
export function matInverse(A) {
  const n = A.length
  const aug = A.map((row, i) => {
    const r = [...row]
    for (let j = 0; j < n; j++) r.push(i === j ? 1 : 0)
    return r
  })

  for (let col = 0; col < n; col++) {
    // Partial pivot
    let maxRow = col
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) maxRow = row
    }
    ;[aug[col], aug[maxRow]] = [aug[maxRow], aug[col]]

    const pivot = aug[col][col]
    if (Math.abs(pivot) < 1e-12) return null // singular

    for (let j = col; j < 2 * n; j++) aug[col][j] /= pivot

    for (let row = 0; row < n; row++) {
      if (row === col) continue
      const factor = aug[row][col]
      for (let j = col; j < 2 * n; j++) aug[row][j] -= factor * aug[col][j]
    }
  }

  return aug.map(row => row.slice(n))
}

// Cholesky decomposition: returns L where L*L^T = A (A must be positive definite)
export function choleskyDecompose(A) {
  const n = A.length
  const L = Array.from({ length: n }, () => new Array(n).fill(0))
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = 0
      for (let k = 0; k < j; k++) sum += L[i][k] * L[j][k]
      if (i === j) {
        const val = A[i][i] - sum
        L[i][j] = val > 1e-12 ? Math.sqrt(val) : 1e-6
      } else {
        L[i][j] = L[j][j] > 1e-12 ? (A[i][j] - sum) / L[j][j] : 0
      }
    }
  }
  return L
}

// Diagonal elements of a matrix
export function matDiag(A) {
  return A.map((row, i) => row[i])
}

// Normal CDF via Abramowitz & Stegun approximation
export function normCDF(x) {
  if (x < -8) return 0
  if (x > 8) return 1
  const t = 1 / (1 + 0.2316419 * Math.abs(x))
  const d = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI)
  const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  const p = 1 - d * poly
  return x >= 0 ? p : 1 - p
}

// Two-tailed p-value from t-statistic and degrees of freedom
// Uses normal approximation for df > 30, otherwise Student's t approximation
export function tPValue(t, df) {
  const absT = Math.abs(t)
  if (df >= 30) return 2 * (1 - normCDF(absT))
  // Cornish-Fisher expansion for small df
  const x = absT / Math.sqrt((df + absT * absT) / df)
  return 2 * (1 - normCDF(x * (1 + x * x / (4 * df))))
}

// F-distribution p-value (approximation via chi-squared)
export function fPValue(f, df1, df2) {
  if (f <= 0) return 1
  const chi2 = f * df1
  const p = chiSqPValue(chi2, df1)
  return p
}

// Chi-squared p-value (one-tailed upper)
export function chiSqPValue(x, df) {
  if (x <= 0) return 1
  // Wilson-Hilferty normal approximation
  const k = df
  const z = Math.pow(x / k, 1 / 3) - (1 - 2 / (9 * k))
  const sigma = Math.sqrt(2 / (9 * k))
  const zscore = z / sigma
  return 1 - normCDF(zscore)
}
